import {
	Button,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
	Select,
} from "@chakra-ui/react";
import { Trash2 } from "lucide-react";
import { PropsWithChildren } from "react";
import { gql } from "../../__generated__";
import { useMutation } from "@apollo/client";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { PortfolioEducation } from "../../types";
import { EducationLevel } from "../../__generated__/graphql";
import { tertiaryQualifications } from "./constants";
import { educationEditorSchema } from "./portfolio-input-validation";

const EDIT_EDUCATION = gql(`
  mutation EditPortfolioEducation($id: String!, $schoolName: String, $fieldOfStudy: String, $qualification: EducationLevel) {
  editPortfolioEducation(id: $id, schoolName: $schoolName, fieldOfStudy: $fieldOfStudy, qualification: $qualification) {
    createdAt
    fieldOfStudy
    id
    qualification
    schoolName
    verified
  }
}
`);

const REMOVE_EDUCATION = gql(`
  mutation RemovePortfolioEducation($id: String!) {
    removePortfolioEducation(id: $id) {
      id
    }
  }
`);

interface EducationEditorProps {
	onClose: () => void;
	item: PortfolioEducation & { id: string };
}

const formSchema = educationEditorSchema;

export const EducationEditor = ({
	onClose,
	item,
}: PropsWithChildren<EducationEditorProps>) => {
	const {
		register,
		watch,
		handleSubmit,
		formState: { errors },
	} = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			schoolName: item.schoolName,
			qualification: item.qualification,
			fieldOfStudy: item.fieldOfStudy || "",
		},
	});

	const [editEducation, editEducationHandle] = useMutation(EDIT_EDUCATION, {
		onCompleted: () => {
			onClose();
		},
	});

	const [removeEducation, removeEducationHandle] = useMutation(
		REMOVE_EDUCATION,
		{
			refetchQueries: ["MyPortfolioWithMedia"],
			awaitRefetchQueries: true,
		}
	);

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		editEducation({
			variables: {
				id: item.id,
				...data,
			},
		});
	};

	const handleRemove = () => {
		removeEducation({
			variables: {
				id: item.id,
			},
		});
	};

	const qualification = watch("qualification");

	return (
		<div className="flex flex-col py-4 gap-4 w-full">
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="flex flex-col gap-8">
					<div className="flex flex-col gap-4">
						<FormControl isInvalid={!!errors.schoolName}>
							<FormLabel>School Name</FormLabel>
							<Input {...register("schoolName")} placeholder="School Name" />
							<FormErrorMessage>{errors.schoolName?.message}</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!errors.qualification}>
							<FormLabel>Qualification</FormLabel>
							<Select
								id="qualification"
								{...register("qualification")}
								variant="outline"
								rounded={0}
								border={"1px solid #D7D7D7"}
							>
								<option value={EducationLevel.Jssce}>
									Junior Secondary School Certificate (JSSCE)
								</option>
								<option value={EducationLevel.Ssce}>
									Senior School Certificate (e.g WAEC, NECO, GCE)
								</option>
								<option value={EducationLevel.Nabteb}>
									National Business and Technical Examination Board Certificate
									(NABTEB)
								</option>
								<option value={EducationLevel.Ond}>
									Ordinary National Diploma (OND)
								</option>
								<option value={EducationLevel.Hnd}>
									Higher National Diploma (HND)
								</option>
								<option value={EducationLevel.Bachelors}>
									Bachelor's Degree (BSc)
								</option>
								<option value={EducationLevel.Masters}>
									Master's Degree (MSc)
								</option>
								<option value={EducationLevel.Doctorate}>
									Doctorate Degree (PhD)
								</option>
							</Select>
							<FormErrorMessage>
								{errors.qualification?.message}
							</FormErrorMessage>
						</FormControl>

						{tertiaryQualifications.includes(
							qualification as EducationLevel
						) && (
							<FormControl isInvalid={!!errors.fieldOfStudy}>
								<FormLabel>Field of Study</FormLabel>
								<Input
									{...register("fieldOfStudy")}
									placeholder="Field of Study"
								/>
								<FormErrorMessage>
									{errors.fieldOfStudy?.message}
								</FormErrorMessage>
							</FormControl>
						)}
					</div>
					<div className="flex justify-between">
						<Button
							colorScheme="red"
							variant="outline"
							leftIcon={<Trash2 className="w-5 h-5" />}
							isLoading={removeEducationHandle.loading}
							onClick={handleRemove}
						>
							Delete
						</Button>
						<div>
							<Button variant="ghost" mr={3} onClick={onClose}>
								Close
							</Button>
							<Button
								colorScheme="brand"
								isLoading={editEducationHandle.loading}
								type="submit"
							>
								Save
							</Button>
						</div>
					</div>
				</div>
			</form>
		</div>
	);
};
